import { clsx } from "clsx";
import type { ReactNode } from "react";

/** The quiet bordered micro-label (design language: chips for inline labels). One
 * implementation for the roster's name-cell chips, the entity page's facet row, the items
 * list's faction label and the search palette's race chip — they had already drifted apart in
 * padding and ink before this was shared, and three of them were still hand-rolled beside it
 * until the 2026-09-08 review (finding U14).
 *
 * The drift is preserved, not resolved: this was a behaviour-preserving change, so each caller
 * still says what it renders. Two of the overrides carry `!` because Tailwind emits its
 * utilities in value order, not source order, so `py-0.5` and `text-ink-2` would lose the
 * cascade to the base's `py-px` and `text-ink-3` — measured in the built CSS 2026-09-08.
 *
 * A titled chip does NOT take `cursor-help` (2026-09-07). In this app that cursor is half of
 * a pair — it goes with the dotted underline, and together they mean "there is honesty
 * language one layer down" on a stat cell. A chip carries neither: it is a label whose title
 * merely spells out an abbreviation ("DLC" → "Requires Harbinger DLC"), and a bordered pill
 * cannot wear the underline that would make the promise legible. So the help cursor here was
 * an unbacked promise — and since an untitled chip sits right beside a titled one (the unit
 * kind has no title), which chips offered it read as arbitrary. The title stays and still
 * shows on hover; only the cursor goes. */
export function Chip({
  label,
  title,
  sigil,
  className,
}: {
  label: string;
  title?: string;
  /** A leading glyph inside the chip's box — today the race sigil, which is the design
   * language's race-identity treatment anywhere race appears outside its own column. The
   * caller styles the node, including its font where that must differ from the chip's mono:
   * the sigil's rendered width is font-dependent (■ measures 0.86em in IBM Plex Sans against
   * 0.55em in IBM Plex Mono, read off the served build 2026-09-08). */
  sigil?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "rounded border border-line px-1 py-px font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3",
        sigil !== undefined && "inline-flex items-baseline",
        className,
      )}
      title={title}
    >
      {sigil}
      {label}
    </span>
  );
}
